import React, { useState, useReducer } from 'react';
import styled from 'styled-components';

const initialState = {
  currentDate: new Date(2024, 7), // August 2024
  viewMode: 'month', // 'month' or 'week'
  selectedDay: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'PREVIOUS':
      return {
        ...state,
        currentDate: state.viewMode === 'month'
          ? new Date(state.currentDate.setMonth(state.currentDate.getMonth() - 1))
          : new Date(state.currentDate.setDate(state.currentDate.getDate() - 7))
      };
    case 'NEXT':
      return {
        ...state,
        currentDate: state.viewMode === 'month'
          ? new Date(state.currentDate.setMonth(state.currentDate.getMonth() + 1))
          : new Date(state.currentDate.setDate(state.currentDate.getDate() + 7))
      };
    case 'TOGGLE_VIEW_MODE':
      return {
        ...state,
        viewMode: state.viewMode === 'month' ? 'week' : 'month'
      };
    case 'SELECT_DAY':
      return {
        ...state,
        selectedDay: action.payload
      };
    default:
      return state;
  }
};

const AllSchedule = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth(state.currentDate.getFullYear(), state.currentDate.getMonth()) }, (_, i) => i + 1);

  const events = [
    { date: 2, type: 'event', description: 'Mock Event 1', time: '10:00' },
    { date: 5, type: 'event', description: 'Mock Event 2', time: '14:00' },
    { date: 10, type: 'reminder', description: 'Mock Reminder 1', time: '09:00' },
    { date: 15, type: 'event', description: 'Mock Event 3', time: '16:00' },
    { date: 20, type: 'reminder', description: 'Mock Reminder 2', time: '11:00' },
    { date: 25, type: 'event', description: 'Mock Event 4', time: '13:00' },
    { date: 30, type: 'reminder', description: 'Mock Reminder 3', time: '08:00' }
  ];

  const renderDays = () => {
    if (state.viewMode === 'month') {
      return days.map((day) => (
        <Day key={day} onClick={() => dispatch({ type: 'SELECT_DAY', payload: day })}>
          {day}
          {events.filter(event => event.date === day).map(event => (
            <Event key={event.description} type={event.type}>{event.description}</Event>
          ))}
        </Day>
      ));
    } else {
      const startWeek = new Date(state.currentDate);
      startWeek.setDate(state.currentDate.getDate() - state.currentDate.getDay());
      const weekDays = Array.from({ length: 7 }, (_, i) => new Date(startWeek).setDate(startWeek.getDate() + i));

      return (
        <>
          <TimeAxis>
            {Array.from({ length: 24 }, (_, i) => (
              <TimeSlot key={i}>{`${i}:00`}</TimeSlot>
            ))}
          </TimeAxis>
          {weekDays.map((day, index) => (
            <WeekDay key={day} onClick={() => dispatch({ type: 'SELECT_DAY', payload: new Date(day).getDate() })}>
              {new Date(day).getDate()}
              {events.filter(event => new Date(day).getDate() === event.date).map(event => (
                <WeekEvent key={event.description} type={event.type} time={event.time}>{event.description}</WeekEvent>
              ))}
            </WeekDay>
          ))}
        </>
      );
    }
  };

  const renderSelectedDayEvents = () => {
    if (state.selectedDay === null) return null;
    const selectedEvents = events.filter(event => event.date === state.selectedDay);
    return (
      <EventDetails>
        <h3>Events on {state.selectedDay}:</h3>
        {selectedEvents.length === 0 ? (
          <p>No events.</p>
        ) : (
          selectedEvents.map(event => (
            <p key={event.description}>{event.description} at {event.time}</p>
          ))
        )}
      </EventDetails>
    );
  };

  return (
    <CalendarContainer>
      <CalendarHeader>
        <button onClick={() => dispatch({ type: 'PREVIOUS' })}>&lt;</button>
        <MonthYear>{`${state.currentDate.getFullYear()}年${state.currentDate.getMonth() + 1}月`}</MonthYear>
        <button onClick={() => dispatch({ type: 'NEXT' })}>&gt;</button>
        <button onClick={() => dispatch({ type: 'TOGGLE_VIEW_MODE' })}>
          {state.viewMode === 'month' ? 'Switch to Week View' : 'Switch to Month View'}
        </button>
      </CalendarHeader>
      <Content>
        <CalendarGrid>
          {renderDays()}
        </CalendarGrid>
        {renderSelectedDayEvents()}
      </Content>
    </CalendarContainer>
  );
};

const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: auto;
  padding-top: 60px; 
`;

const CalendarHeader = styled.div`
  width: 50%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #f0f0f0;
  position: fixed; /* Make the header fixed */
  top: 0; /* Stick to the top of the page */
  z-index: 1; /* Ensure it stays above the grid */
  max-width: 1200px;
  margin: auto;
  left: 0;
  right: 0;
`;

const MonthYear = styled.div`
  font-size: 24px;
  font-weight: bold;
`;

const Content = styled.div`
  display: flex;
  width: 100%;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  width: 70%;
  border-top: 1px solid #ddd;
  border-left: 1px solid #ddd;
`;

const EventDetails = styled.div`
  width: 30%;
  padding: 20px;
  background-color: #f9f9f9;
  border-left: 1px solid #ddd;
`;

const TimeAxis = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ddd;
`;

const TimeSlot = styled.div`
  height: 60px;
  border-bottom: 1px solid #ddd;
  text-align: center;
  line-height: 60px;
`;

const Day = styled.div`
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 10px;
  text-align: center;
  min-height: 100px;
  position: relative;
  cursor: pointer;
`;

const WeekDay = styled.div`
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  text-align: center;
  min-height: 1440px; /* 24 hours * 60 minutes */
  position: relative;
  cursor: pointer;
`;

const Event = styled.div`
  background-color: ${(props) => (props.type === 'event' ? '#ffcccc' : '#ccffcc')};
  margin-top: 5px;
  padding: 5px;
  border-radius: 3px;
  font-size: 12px;
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  text-align: center;
`;

const WeekEvent = styled.div`
  background-color: ${(props) => (props.type === 'event' ? '#ffcccc' : '#ccffcc')};
  margin-top: 5px;
  padding: 5px;
  border-radius: 3px;
  font-size: 12px;
  position: absolute;
  top: ${(props) => {
    const [hour, minute] = props.time.split(':');
    return `${(parseInt(hour, 10) * 60 + parseInt(minute, 10)) / 1440 * 100}%`;
  }};
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  text-align: center;
`;

export default AllSchedule;
